import React from 'react';
import { X, CheckCircle, XCircle, AlertCircle, Download, Clock, BarChart3 } from 'lucide-react';

const TestResultsModal = ({ isOpen, onClose, testResults }) => {
  if (!isOpen || !testResults) return null;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pass':
        return <CheckCircle size={16} style={{ color: '#10B981' }} />;
      case 'fail':
        return <XCircle size={16} style={{ color: '#EF4444' }} />;
      case 'error':
        return <AlertCircle size={16} style={{ color: '#F59E0B' }} />;
      default:
        return <AlertCircle size={16} style={{ color: '#6B7280' }} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pass':
        return '#10B981';
      case 'fail':
        return '#EF4444';
      case 'error':
        return '#F59E0B';
      default:
        return '#6B7280';
    }
  };

  const exportResults = () => {
    const blob = new Blob([JSON.stringify(testResults, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `test-results-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        maxWidth: '90vw',
        maxHeight: '90vh',
        width: '800px',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <BarChart3 size={24} style={{ color: '#007B8F' }} />
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#1F2937',
              margin: 0
            }}>
              Test Results
            </h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={exportResults}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                backgroundColor: '#F3F4F6',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151'
              }}
            >
              <Download size={16} />
              Export
            </button>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <X size={20} style={{ color: '#6B7280' }} />
            </button>
          </div>
        </div>

        {/* Summary */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #E5E7EB',
          backgroundColor: '#F9FAFB'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '16px'
          }}>
            <div style={{
              textAlign: 'center',
              padding: '16px',
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #E5E7EB'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#1F2937' }}>
                {testResults.summary?.total || testResults.results?.length || 0}
              </div>
              <div style={{ fontSize: '14px', color: '#6B7280' }}>Total Tests</div>
            </div>
            <div style={{
              textAlign: 'center',
              padding: '16px',
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #E5E7EB'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#10B981' }}>
                {testResults.summary?.passed || testResults.results?.filter(r => r.status === 'pass').length || 0}
              </div>
              <div style={{ fontSize: '14px', color: '#6B7280' }}>Passed</div>
            </div>
            <div style={{
              textAlign: 'center',
              padding: '16px',
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #E5E7EB'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#EF4444' }}>
                {testResults.summary?.failed || testResults.results?.filter(r => r.status === 'fail').length || 0}
              </div>
              <div style={{ fontSize: '14px', color: '#6B7280' }}>Failed</div>
            </div>
            <div style={{
              textAlign: 'center',
              padding: '16px',
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #E5E7EB'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#F59E0B' }}>
                {testResults.summary?.errors || testResults.results?.filter(r => r.status === 'error').length || 0}
              </div>
              <div style={{ fontSize: '14px', color: '#6B7280' }}>Errors</div>
            </div>
          </div>
          
          {testResults.summary?.duration && (
            <div style={{
              marginTop: '16px',
              textAlign: 'center',
              fontSize: '14px',
              color: '#6B7280',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              <Clock size={16} />
              Total Duration: {testResults.summary.duration}ms
            </div>
          )}
        </div>

        {/* Test Results */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: '24px'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {(testResults.results || []).map((result, index) => (
              <div
                key={index}
                style={{
                  padding: '16px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  backgroundColor: 'white'
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '8px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {getStatusIcon(result.status)}
                    <span style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#1F2937'
                    }}>
                      {result.testName}
                    </span>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    fontSize: '14px',
                    color: '#6B7280'
                  }}>
                    {result.duration && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Clock size={14} />
                        {result.duration}ms
                      </span>
                    )}
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      backgroundColor: getStatusColor(result.status) + '20',
                      color: getStatusColor(result.status),
                      fontSize: '12px',
                      fontWeight: '500',
                      textTransform: 'uppercase'
                    }}>
                      {result.status}
                    </span>
                  </div>
                </div>
                
                <div style={{
                  fontSize: '14px',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  {result.message}
                </div>

                {result.data && (
                  <details style={{ marginTop: '8px' }}>
                    <summary style={{
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#6B7280',
                      fontWeight: '500'
                    }}>
                      View Details
                    </summary>
                    <pre style={{
                      marginTop: '8px',
                      padding: '12px',
                      backgroundColor: '#F9FAFB',
                      borderRadius: '6px',
                      fontSize: '12px',
                      color: '#374151',
                      overflow: 'auto',
                      maxHeight: '200px'
                    }}>
                      {JSON.stringify(result.data, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #E5E7EB',
          backgroundColor: '#F9FAFB',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ fontSize: '14px', color: '#6B7280' }}>
            Generated on {new Date(testResults.summary?.timestamp || Date.now()).toLocaleString()}
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              backgroundColor: '#007B8F',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestResultsModal;
